import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Link } from "react-router-dom";
import TableContainer from "Common/TableContainer";
import Select from "react-select";
import { Loader } from "../../Common/Loader";
import AddSalaryModal from "../../components/AddSalaryModal";
// Icons
import { Calendar, Search, Users, DollarSign, CreditCard, Edit, Plus } from "lucide-react";
// API Helpers
import { getTeacherList, getYearlySalary, saveSalary, updateSalary, getAcademicYearList } from "helpers/backend_helper";
// Stores
import { useProgramStore } from "store/useProgramStore";
import { useClassStore } from "store/useClassStore";
import { useAcademicYearStore } from "store/useAcademicYearStore";
import { useAuthStore } from "store/useAuthStore";

// Default options for dropdowns
const defaultProgramOption = { value: "", label: "All Programs" };
const defaultClassOption = { value: "", label: "All Classes" };
const defaultTeacherOption = { value: "", label: "All Teachers" };

// Month names for columns
const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

// Function to generate month headers based on AcademicYear dates
const generateMonthHeaders = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) return [];
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const headers = [];
  
  let current = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  
  while (current <= endMonth) {
    const monthName = monthNames[current.getMonth()];
    const year = current.getFullYear();
    headers.push({
      month: current.getMonth() + 1,
      year: year,
      label: `${monthName} ${year}`,
      date: new Date(current)
    });
    
    current.setMonth(current.getMonth() + 1);
  }
  
  return headers;
};

function SalaryList() {
  // Zustand stores
  const { 
    programs, 
    selectedProgram, 
    isLoading: isLoadingPrograms, 
    fetchPrograms, 
    setSelectedProgram 
  } = useProgramStore();
  
  const { 
    classes, 
    selectedClass, 
    isLoading: isLoadingClasses, 
    setSelectedClass 
  } = useClassStore();

  const { 
    academicYears,
    selectedAcademicYear,
    setSelectedAcademicYear,
  } = useAcademicYearStore();

  const [salaryData, setSalaryData] = useState<any[]>([]);
  const [filteredSalary, setFilteredSalary] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState<any>(defaultTeacherOption);

  // API Data States
  const [teacherList, setTeacherList] = useState<any[]>([defaultTeacherOption]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState<boolean>(true);
  const [yearlySalaryData, setYearlySalaryData] = useState<any[]>([]);
  const [isLoadingSalary, setIsLoadingSalary] = useState<boolean>(false);
  const [academicYearDetails, setAcademicYearDetails] = useState<any>(null);
  const [monthHeaders, setMonthHeaders] = useState<any[]>([]);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedSalaryData, setSelectedSalaryData] = useState<any>(null);

  const { user } = useAuthStore();

  // AcademicYear options
  const academicYearOptions = useMemo(() => {
    const options = academicYears.map((academicYear: any) => ({
      value: academicYear.academicYearId,
      label: academicYear.academicYearName
    }));
    return options;
  }, [academicYears]);

  const selectedAcademicYearOption = useMemo(() => {
    if (!selectedAcademicYear) return { value: "", label: "Select Academic Year" };
    return {
      value: selectedAcademicYear.academicYearId,
      label: selectedAcademicYear.academicYearName
    };
  }, [selectedAcademicYear]);

  // Stats calculations
  const totalTeachers = filteredSalary.length;
  const totalPaid = filteredSalary.reduce((total, teacher) => {
    let teacherTotal = 0;
    teacher.monthlySalaries?.forEach((monthSalary: any) => {
      if (monthSalary.salaryId > 0 && monthSalary.totalSalary > 0) {
        teacherTotal += monthSalary.totalSalary || 0;
      }
    });
    return total + teacherTotal;
  }, 0);

  const totalPending = filteredSalary.reduce((total, teacher) => {
    let pendingCount = 0;
    teacher.monthlySalaries?.forEach((monthSalary: any) => {
      if (monthSalary.salaryId === 0 || (monthSalary.salaryId > 0 && (monthSalary.totalSalary === null || monthSalary.totalSalary === 0))) {
        pendingCount++;
      }
    });
    return total + pendingCount;
  }, 0);

  const avgSalaryPerTeacher = totalTeachers > 0 ? Math.round(totalPaid / totalTeachers) : 0;

  const handleProgramChange = (option: any) => {
    setSelectedProgram(option);
  };

  const handleClassChange = (option: any) => {
    setSelectedClass(option);
  };

  const handleTeacherChange = (option: any) => {
    setSelectedTeacher(option);
  };

  const handleAcademicYearChange = (option: any) => {
    if (option && option.value && option.value !== "") {
      const academicYear = academicYears.find(ay => ay.academicYearId === option.value);
      if (academicYear) {
        setSelectedAcademicYear(academicYear);
        console.log("Academic Year selected:", academicYear);
      }
    } else {
      setSelectedAcademicYear(null);
      console.log("Academic Year cleared");
    }
  };

  // Modal handlers
  const handleEditSalary = (teacher: any, month: number, year?: number) => {
    const monthSalary = teacher.monthlySalaries?.find((salary: any) => salary.month === month && salary.year === year);
    const monthName = monthNames[month - 1];
    
    console.log("monthSalary:", teacher);
    setSelectedSalaryData({
      teacherId: teacher.teacherId,
      teacherName: teacher.teacherName,
      month: month,
      monthName: monthName,
      year: year,
      monthYear: `${monthName} ${year}`,
      programName: selectedProgram?.programName || 'All Programs',
      className: selectedClass?.className || 'All Classes',
      existingData: monthSalary ? {
        salaryId: monthSalary.salaryId,
        totalSalary: monthSalary.totalSalary,
        hoursWorked: monthSalary.hoursWorked,
        salaryGivenOn: monthSalary.salaryGivenOn,
        notes: monthSalary.notes,
        paymentMethodId: monthSalary.paymentMethodId,
        salaryType: monthSalary.salaryType,
        rate: monthSalary.rate,
        updatedOn:monthSalary.updatedOn,
        hoursSubmitted:monthSalary.hoursSubmitted
      } : null
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSalaryData(null);
  };

  const handleSaveSalary = async (salaryData: any) => {
    try {
      // Create proper date format: YYYY-MM-01 (first day of the month)
      const monthStr = salaryData.month.toString().padStart(2, '0');
      const salaryMonthYear = `${salaryData.year}-${monthStr}-01T00:00:00.000Z`;
      
      const payload = {
        clientId: 1,
        programId: selectedProgram?.programId,
        classId: selectedClass?.classId,
        teacherId: salaryData.teacherId,
        month: salaryData.month,
        year: salaryData.year,
        salaryMonthYear: salaryMonthYear,
        totalSalary: salaryData.totalSalary,
        hoursWorked: salaryData.hoursWorked,
        salaryGivenOn: salaryData.salaryGivenOn,
        notes: salaryData.notes,
        paymentMethodId: salaryData.paymentMethodId,
        createdBy: user?.userId // This should come from user context
      };

      let response;
      if (salaryData.salaryId && salaryData.salaryId > 0) {
        // Update existing salary
        response = await updateSalary({
          ...payload,
          salaryId: salaryData.salaryId
        });
      } else {
        // Create new salary
        response = await saveSalary(payload);
      }

      console.log("Salary save response:", response);
      
      // Reload the salary data to reflect changes
      await loadYearlySalary();
      
    } catch (error) {
      console.error("Error saving salary:", error);
      throw error;
    }
  };





  // Load Yearly Salary from API
  const loadYearlySalary = async () => {
    if (!selectedProgram?.programId || !selectedClass?.classId || !academicYearDetails?.startDate || !academicYearDetails?.endDate) {
      setYearlySalaryData([]);
      setFilteredSalary([]);
      return;
    }

    setIsLoadingSalary(true);
    try {
      const response = await getYearlySalary({
        programId: selectedProgram.programId,
        classId: selectedClass.classId,
        teacherId: selectedTeacher.value || null,
        clientId: 1, // Assuming clientId is 1, you might want to get this from user context
        startDate: academicYearDetails.startDate,
        endDate: academicYearDetails.endDate
      });
      
      console.log("Yearly salary response:", response);
      
      if (response.data && Array.isArray(response.data)) {
        setYearlySalaryData(response.data);
        setSalaryData(response.data);
        setFilteredSalary(response.data);
      } else {
        setYearlySalaryData([]);
        setSalaryData([]);
        setFilteredSalary([]);
      }
    } catch (error) {
      console.error("Error loading yearly salary:", error);
      setYearlySalaryData([]);
      setSalaryData([]);
      setFilteredSalary([]);
    } finally {
      setIsLoadingSalary(false);
    }
  };

  // Load AcademicYear details and generate month headers
  useEffect(() => {
    const loadAcademicYearDetails = async () => {
      if (selectedAcademicYear?.academicYearId) {
        try {
          const response = await getAcademicYearList();
          const academicYear = response.data.find((ay: any) => ay.academicYearId === selectedAcademicYear.academicYearId);
          
          if (academicYear && academicYear.startDate && academicYear.endDate) {
            setAcademicYearDetails(academicYear);
            const headers = generateMonthHeaders(academicYear.startDate, academicYear.endDate);
            setMonthHeaders(headers);
            console.log("Generated month headers:", headers);
          }
        } catch (error) {
          console.error("Error loading AcademicYear details:", error);
        }
      } else {
        setAcademicYearDetails(null);
        setMonthHeaders([]);
      }
    };

    loadAcademicYearDetails();
  }, [selectedAcademicYear]);

  useEffect(() => {
    // Load dropdown data
    const loadData = async () => {
      setIsLoadingDropdowns(true);
      console.log("Starting to load dropdown data...");
      
      // Load dropdowns in parallel
      setIsLoadingDropdowns(false);
      setIsLoading(false);
      console.log("Finished loading dropdown data");
    };

    loadData();
  }, []);

  // Load salary data when filters change
  useEffect(() => {
    if (selectedProgram?.programId && selectedClass?.classId && academicYearDetails?.startDate && academicYearDetails?.endDate) {
      loadYearlySalary();
    } else {
      setSalaryData([]);
      setFilteredSalary([]);
    }
  }, [selectedProgram, selectedClass, selectedTeacher, academicYearDetails]);

  useEffect(() => {
    // Filter salary data based on search text
    const filtered = salaryData.filter((teacher: any) => {
      const searchMatch = searchText === "" || 
        teacher.teacherName.toLowerCase().includes(searchText.toLowerCase());
      return searchMatch;
    });
    setFilteredSalary(filtered);
  }, [salaryData, searchText]);

  // Debug effect to log dropdown data
  useEffect(() => {
    console.log("Program list updated:", programs);
    console.log("Class list updated:", classes);
    console.log("Teacher list updated:", teacherList);
  }, [programs, classes, teacherList]);

  // Generate columns for the table
  const columns = useMemo(() => {
    const cols = [
      // {
      //   header: "Teacher ID",
      //   accessorKey: "teacherId",
      //   enableColumnFilter: false,
      //   cell: (cell: any) => (
      //     <div className="font-medium text-slate-900 dark:text-zink-50">
      //       #{cell.getValue()}
      //     </div>
      //   ),
      // },
      {
        header: "Teacher Name",
        accessorKey: "teacherName",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div className="font-medium text-slate-900 dark:text-zink-50">
            {cell.getValue()}
          </div>
        ),
      }
    ];

    // Add columns for each month based on AcademicYear dates
    if (monthHeaders.length > 0) {
      monthHeaders.forEach((header) => {
        cols.push({
          header: header.label,
          accessorKey: `month_${header.month}_${header.year}`,
          enableColumnFilter: false,
          cell: (cell: any) => {
            const row = cell.row.original;
            const monthSalary = row.monthlySalaries?.find((salary: any) => salary.month === header.month && salary.year === header.year);
          
           if (monthSalary && monthSalary.salaryId > 0 && monthSalary.totalSalary > 0) {
             // Paid - show amount
             return (
               <div className="text-center">
                 <div className="mb-1">
                   <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                     ${monthSalary.totalSalary.toFixed(2)}
                   </span>
                 </div>
                 {monthSalary.hoursWorked > 0 && (
                   <div className="text-xs text-gray-500 mb-1">
                     {monthSalary.hoursWorked}h
                   </div>
                 )}
                 <button
                   onClick={() => handleEditSalary(row, header.month, header.year)}
                   className="inline-flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20"
                   title="Edit salary"
                 >
                   <Edit className="size-3 mr-1" />
                   Edit
                 </button>
               </div>
             );
           } else if (monthSalary && monthSalary.salaryId > 0 && (monthSalary.totalSalary === null || monthSalary.totalSalary === 0)) {
             // Has salary record but not paid
             return (
               <div className="text-center">
                 <div className="mb-1">
                   <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                     Pending
                   </span>
                 </div>
                 <button
                   onClick={() => handleEditSalary(row, header.month, header.year)}
                   className="inline-flex items-center px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20"
                   title="Edit salary"
                 >
                   <Edit className="size-3 mr-1" />
                   Edit
                 </button>
               </div>
             );
           } else {
             // No salary record - show add button
             return (
               <div className="text-center">
                 <button
                   onClick={() => handleEditSalary(row, header.month, header.year)}
                   className="inline-flex items-center px-2 py-1 text-xs text-green-600 hover:text-green-800 hover:bg-green-50 rounded dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20"
                   title="Add salary"
                 >
                   <Plus className="size-3 mr-1" />
                   Add
                 </button>
               </div>
             );
           }
        },
      });
      });
    } else {
      // Fallback to default month columns if no AcademicYear is selected
      monthNames.forEach((monthName, index) => {
        const monthNumber = index + 1;
        cols.push({
          header: monthName,
          accessorKey: `month_${monthNumber}`,
          enableColumnFilter: false,
          cell: (cell: any) => (
            <div className="text-center text-gray-400">
              No Academic Year selected
            </div>
          ),
        });
      });
    }

    return cols;
  }, [monthHeaders]);

  return (
    <React.Fragment>
      {/* <BreadCrumb title="Teacher Salary" pageTitle="Salary" /> */}
      <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
        <div className="flex flex-col gap-2 py-4 md:py-6">
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2 xl:grid-cols-4">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-blue-100 rounded-md dark:bg-blue-500/20">
                      <Users className="size-6 text-blue-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{totalTeachers}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Total Teachers</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-green-100 rounded-md dark:bg-green-500/20">
                      <DollarSign className="size-6 text-green-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">${totalPaid.toLocaleString()}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Total Paid</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-red-100 rounded-md dark:bg-red-500/20">
                      <CreditCard className="size-6 text-red-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{totalPending}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Pending Payments</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-yellow-100 rounded-md dark:bg-yellow-500/20">
                      <DollarSign className="size-6 text-yellow-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">${avgSalaryPerTeacher.toLocaleString()}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Avg per Teacher</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Card */}
          <div className="card">
            <div className="card-body">
              
              {/* Filters Section */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 xl:grid-cols-12 mb-5">
                
                {/* Search */}
                <div className="lg:col-span-2">
                  <div className="relative">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search for ..."
                      autoComplete="off"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>

                {/* Program Filter */}
                {/* <div className="lg:col-span-2">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Program"
                    name="program"
                    options={[
                      { value: null, label: "All Programs" },
                      ...programs.map(program => ({
                        value: program,
                        label: program.programName
                      }))
                    ]}
                    value={selectedProgram ? { value: selectedProgram, label: selectedProgram.programName } : { value: null, label: "All Programs" }}
                    onChange={(option) => handleProgramChange(option?.value || null)}
                    isLoading={isLoadingPrograms}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div> */}

                {/* Class Filter */}
                {/* <div className="lg:col-span-2">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Class"
                    name="class"
                    options={[
                      { value: null, label: "All Classes" },
                      ...classes.map(classItem => ({
                        value: classItem,
                        label: classItem.className
                      }))
                    ]}
                    value={selectedClass ? { value: selectedClass, label: selectedClass.className } : { value: null, label: "All Classes" }}
                    onChange={(option) => handleClassChange(option?.value || null)}
                    isLoading={isLoadingClasses}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div> */}

                {/* Teacher Filter */}
                {/* <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Teacher (Optional)"
                    name="teacher"
                    options={teacherList}
                    value={selectedTeacher}
                    onChange={handleTeacherChange}
                    isLoading={isLoadingDropdowns}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div> */}

                {/* Academic Year Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Academic Year"
                    name="academicYear"
                    options={academicYearOptions}
                    value={selectedAcademicYearOption}
                    onChange={handleAcademicYearChange}
                    isLoading={academicYears.length === 0}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>
              </div>

              {/* Salary Table */}
              {isLoading || isLoadingSalary ? (
                <Loader />
              ) : !selectedProgram || !selectedClass || !selectedAcademicYear?.academicYearId ? (
                <div className="text-center py-8">
                  <div className="text-slate-500 dark:text-zink-400">
                    <DollarSign className="size-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">Select Filters</p>
                    <p>Please select an Academic Year, Program, and Class to view salary data.</p>
                  </div>
                </div>
              ) : filteredSalary.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-slate-500 dark:text-zink-400">
                    <Users className="size-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No Teachers Found</p>
                    <p>No teachers found for the selected program, class, and year.</p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <TableContainer
                    columns={columns}
                    data={filteredSalary}
                    isPagination={true}
                    divclassName="overflow-x-auto"
                    tableclassName="w-full whitespace-nowrap"
                    theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                    thclassName="px-3 py-2 font-semibold border-b border-slate-200 dark:border-zink-500 text-center"
                    tdclassName="px-3 py-2 border-y border-slate-200 dark:border-zink-500"
                    trclassName="even:bg-slate-50 dark:odd:bg-zink-700 dark:even:bg-zink-600"
                  />
                </div>
              )}

            </div>
          </div>
         </div>
       </div>

       {/* Add Salary Modal */}
       {selectedSalaryData && (
         <AddSalaryModal
           isOpen={isModalOpen}
           onClose={handleCloseModal}
           onSave={handleSaveSalary}
           programName={selectedSalaryData.programName}
           className={selectedSalaryData.className}
           teacherName={selectedSalaryData.teacherName}
           teacherId={selectedSalaryData.teacherId}
           monthYear={selectedSalaryData.monthYear}
           month={selectedSalaryData.month}
           year={selectedSalaryData.year}
           existingData={selectedSalaryData.existingData}
         />
       )}
     </React.Fragment>
   );
}

export default SalaryList;

